import { useContext, useEffect, useMemo, useState } from 'react';
import { Model } from 'survey-core';
import 'survey-core/survey-core.css';
import { Survey } from 'survey-react-ui';
import { SharpDark } from 'survey-core/themes';
import { useLocation, useParams } from 'react-router-dom';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import BrandHeader from '../../../components/BrandHeader';
import { AppVarsContext } from '../../../context/AppVarsContext';

const buildSurveyTheme = (theme) => ({
    ...SharpDark,
    cssVariables: {
        ...SharpDark.cssVariables,
        '--sjs-font-family': theme.typography.fontFamily,
        '--sjs-font-size': '16px',
        '--sjs-base-unit': '8px',
        '--sjs-corner-radius': '14px',
        '--sjs-general-backcolor': 'transparent',
        '--sjs-general-backcolor-dark': '#ffffff',
        '--sjs-general-backcolor-dim': '#f1f8fb',
        '--sjs-general-backcolor-dim-light': '#ffffff',
        '--sjs-general-backcolor-dim-dark': '#e3f3f8',
        '--sjs-general-forecolor': theme.palette.text.primary,
        '--sjs-general-forecolor-light': theme.palette.text.secondary,
        '--sjs-general-dim-forecolor': theme.palette.text.primary,
        '--sjs-general-dim-forecolor-light': theme.palette.text.secondary,
        '--sjs-primary-backcolor': theme.palette.primary.main,
        '--sjs-primary-backcolor-light': alpha(theme.palette.primary.main, 0.12),
        '--sjs-primary-backcolor-dark': '#007bb5',
        '--sjs-primary-forecolor': '#ffffff',
        '--sjs-secondary-backcolor': theme.palette.secondary.main,
        '--sjs-secondary-backcolor-light': alpha(theme.palette.secondary.main, 0.12),
        '--sjs-secondary-backcolor-semi-light': alpha(theme.palette.secondary.main, 0.22),
        '--sjs-secondary-forecolor': '#ffffff',
        '--sjs-border-light': alpha(theme.palette.primary.dark, 0.1),
        '--sjs-border-default': alpha(theme.palette.primary.dark, 0.18),
        '--sjs-border-inside': alpha(theme.palette.primary.dark, 0.1),
        '--sjs-shadow-small': 'none',
        '--sjs-shadow-medium': `0 10px 24px ${alpha(theme.palette.primary.dark, 0.08)}`,
        '--sjs-shadow-large': `0 24px 60px ${alpha(theme.palette.primary.dark, 0.12)}`,
        '--sjs-special-red': theme.palette.error.main,
        '--sjs-special-green': theme.palette.secondary.main,
        '--sjs-special-blue': theme.palette.primary.main,
        '--sjs-special-yellow': theme.palette.warning.main,
    },
});

const createResultID = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const SurveyPage = () => {
    const theme = useTheme();
    const location = useLocation();
    const { id: surveyId = '' } = useParams();
    const { appVars, appLoading, appID } = useContext(AppVarsContext);
    const [surveyState, setSurveyState] = useState(null);
    const [surveyRecord, setSurveyRecord] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const responseValue = useMemo(() => new URLSearchParams(location.search).get('r') || '', [location.search]);
    const surveyTheme = useMemo(() => buildSurveyTheme(theme), [theme]);

    useEffect(() => {
        if (appLoading || !appVars?.api_url || !surveyId) return;
        let isActive = true;

        const fetchSurvey = async () => {
            setLoading(true);
            setError('');
            try {
                const response = await fetch(`${appVars.api_url}/survey/${surveyId}`, { method: 'GET', redirect: 'follow' });
                const data = await response.json();
                const record = Array.isArray(data) ? data[0] : null;
                if (!response.ok || !record?.settings) throw new Error('This survey is not available right now.');

                document.title = `${record.name || 'Survey'} | DigitalWerks`;
                const surveyModel = new Model(JSON.parse(record.settings));
                surveyModel.onComplete.add(async (sender) => {
                    try {
                        await fetch(`${appVars.api_url}/survey/result`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ resultID: createResultID(), surveyID: surveyId, result: JSON.stringify(sender.data, null, 3) }),
                            redirect: 'follow',
                        });
                    } catch (submitError) {
                        console.error(submitError);
                    }
                });
                surveyModel.applyTheme(surveyTheme);
                surveyModel.data = responseValue ? { helpful: responseValue } : {};
                if (!isActive) return;
                setSurveyRecord(record);
                setSurveyState(surveyModel);
            } catch (fetchError) {
                if (!isActive) return;
                setSurveyRecord(null);
                setSurveyState(null);
                setError(fetchError?.message || 'This survey is not available right now.');
            } finally {
                if (isActive) setLoading(false);
            }
        };

        fetchSurvey();
        return () => { isActive = false; };
    }, [appID, appLoading, appVars?.api_url, responseValue, surveyId, surveyTheme]);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f1f8fb', backgroundImage: 'radial-gradient(circle at 85% 8%, rgba(0,156,222,.12), transparent 30%)' }}>
            <BrandHeader compact />
            <Box
                component="main"
                sx={{
                    width: 'min(1140px, calc(100% - 32px))',
                    mx: 'auto',
                    py: { xs: 2, md: 5 },
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
                    gap: { xs: 3, md: 5 },
                    alignItems: 'start',
                }}
            >
                <Box sx={{ pt: { md: 4 } }}>
                    <Typography variant="overline" sx={{ color: 'primary.main' }}>Your perspective matters</Typography>
                    <Typography variant="h3" sx={{ mt: 1.25, mb: 2, fontSize: { xs: '2rem', md: '2.45rem' } }}>Share what you see.</Typography>
                    <Typography color="text.secondary" sx={{ lineHeight: 1.7, mb: 3 }}>
                        Thoughtful feedback gives teams a clearer view of what is working—and what should change next.
                    </Typography>
                    <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box sx={{ color: 'secondary.main', display: 'flex' }}><CheckRounded fontSize="small" /></Box>
                            <Typography variant="body2">Complete it in one sitting</Typography>
                        </Stack>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box sx={{ color: 'secondary.main', display: 'flex' }}><LockOutlined fontSize="small" /></Box>
                            <Typography variant="body2">Responses are handled with care</Typography>
                        </Stack>
                    </Stack>
                </Box>

                <Paper
                    elevation={0}
                    sx={{
                        minWidth: 0,
                        borderRadius: { xs: 3, md: 4 },
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 24px 70px rgba(11,36,56,.10)',
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                        p: { xs: 1.25, sm: 2.5, md: 3.5 },
                        position: 'relative',
                        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 5, background: 'linear-gradient(90deg, #009cde, #009b8b 72%, #f59a23)' },
                    }}
                >
                    {loading || appLoading ? (
                        <Stack spacing={1.5} sx={{ minHeight: 360, alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress size={30} />
                            <Typography variant="body2" color="text.secondary">Preparing your survey…</Typography>
                        </Stack>
                    ) : error ? (
                        <Stack spacing={1} sx={{ minHeight: 300, alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 2 }}>
                            <Typography variant="h5">Survey unavailable</Typography>
                            <Typography color="text.secondary">{error}</Typography>
                        </Stack>
                    ) : surveyState && surveyRecord ? (
                        <Box
                            sx={{
                                minWidth: 0,
                                '& *, & *::before, & *::after': { boxSizing: 'border-box' },
                                '& .sd-root-modern, & .sd-container-modern, & .sd-body, & .sd-page, & .sd-row, & .sd-question': { minWidth: 0, maxWidth: '100%' },
                                '& .sd-root-modern, & .sd-body, & .sd-header': { background: 'transparent' },
                                '& .sd-container-modern__title': { p: 0, mb: 2 },
                                '& .sd-header__text h3': { fontFamily: "'Sora', sans-serif", fontSize: { xs: '1.55rem', md: '2rem' }, fontWeight: 700, color: 'text.primary', letterSpacing: '-.03em' },
                                '& .sd-header__text h5, & .sd-description': { color: 'text.secondary' },
                                '& .sd-logo__image': { maxHeight: 54, maxWidth: { xs: 150, sm: 190 }, objectFit: 'contain' },
                                '& .sd-body.sd-body--responsive': { p: 0 },
                                '& .sd-page': { p: '0 !important', bgcolor: 'transparent' },
                                '& .sd-question': { p: { xs: '18px 4px', sm: '24px 8px' }, boxShadow: 'none', borderTop: '1px solid rgba(20,43,60,.1)', borderRadius: 0 },
                                '& .sd-question__title': { fontFamily: "'Sora', sans-serif", fontWeight: 600, color: 'text.primary', fontSize: { xs: '1rem', sm: '1.08rem' }, mb: 1 },
                                '& .sd-input, & .sd-dropdown, & .sd-comment__text': { maxWidth: '100%', borderRadius: '12px', border: '1px solid rgba(20,43,60,.2)', bgcolor: '#f8fbfc', color: 'text.primary', boxShadow: 'none' },
                                '& .sd-input:focus, & .sd-dropdown:focus-within, & .sd-comment__text:focus': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(0,156,222,.14)', outline: 'none', bgcolor: '#fff' },
                                '& .sd-rating': { display: 'grid', gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(5, minmax(0, 1fr))' }, gap: 1, width: '100%' },
                                '& .sd-rating__item': { minWidth: 0, borderRadius: '12px', overflow: 'hidden' },
                                '& .sd-rating__item-text': { minWidth: 0, width: '100%', justifyContent: 'center' },
                                '& .sd-btn': { borderRadius: '12px', minHeight: 50, px: 3, fontWeight: 700, bgcolor: 'primary.main', color: '#fff', boxShadow: 'none' },
                                '& .sd-btn:hover': { bgcolor: '#007bb5', transform: 'translateY(-1px)' },
                                '& .sd-btn:focus-visible': { outline: '3px solid rgba(0,156,222,.25)', outlineOffset: 3 },
                                '& .sd-completedpage': { bgcolor: '#f1f8fb', borderRadius: 3, boxShadow: 'none', color: 'text.primary', minHeight: 280 },
                            }}
                        >
                            <Survey logo={surveyRecord.clientid ? `${appVars.api_url}/imageFiles/${surveyRecord.clientid}.png` : ''} title={surveyRecord.name} model={surveyState} />
                        </Box>
                    ) : null}
                </Paper>
            </Box>
            <Typography component="footer" variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', px: 2, pb: 4 }}>
                Survey experience provided by DigitalWerks Innovations.
            </Typography>
        </Box>
    );
};

export default SurveyPage;
